import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, Copy, Download, ExternalLink } from "lucide-react";
import { Section, Toggle } from "./fields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opt-in shareable digital business card: public link + QR + field toggles.
export function BusinessCardSection() {
  const [card, setCard] = useState(null);
  const qrWrapRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/members/me/card`, { withCredentials: true })
      .then((r) => setCard(r.data))
      .catch((e) => console.warn("[BusinessCard] load failed:", e?.message));
  }, []);

  if (!card) return null;

  const cardUrl = card.slug ? `${window.location.origin}/cartao/${card.slug}` : null;

  const patch = async (updates) => {
    try {
      const r = await axios.patch(`${API}/members/me/card`, updates, { withCredentials: true });
      setCard(r.data);
      if ("enabled" in updates) {
        toast.success(updates.enabled ? "Cartão de visita ativado." : "Cartão desativado — o link deixou de funcionar.");
      }
    } catch { toast.error("Erro a guardar."); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      toast.success("Link copiado.");
    } catch { toast.error("Não foi possível copiar."); }
  };

  const downloadQr = () => {
    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `cartao-ciees-${card.slug}.png`;
    a.click();
  };

  return (
    <Section icon={QrCode} title="Cartão de visita digital">
      <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed -mt-1">
        Um link público com o seu contacto profissional, para partilhar por QR code em eventos.
        Só existe enquanto o ativar — pode desativá-lo a qualquer momento. O Ideal Pessoal, a
        Aliança de Amor e a morada <strong>nunca</strong> são incluídos.
      </p>
      <Toggle
        label="Ativar o meu cartão de visita público"
        hint="Cria um link público único (ex.: ciees.pt/cartao/o-seu-nome) com QR code."
        checked={card.enabled}
        onChange={(v) => patch({ enabled: v })}
        testid="card-enable-toggle"
      />

      {card.enabled && cardUrl && (
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-5">
          <div ref={qrWrapRef} className="bg-white p-3 border border-[hsl(var(--border))] justify-self-center" data-testid="card-qr">
            <QRCodeCanvas value={cardUrl} size={168} level="M" fgColor="#0E4D45" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">O seu link</div>
            <div className="mt-1 text-xs font-mono break-all text-[hsl(var(--foreground))]" data-testid="card-url">{cardUrl}</div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[hsl(var(--primary))] text-white text-[11px] uppercase tracking-[0.16em] hover:bg-[hsl(var(--primary))]/90" data-testid="card-copy-link">
                <Copy className="h-3.5 w-3.5" /> Copiar link
              </button>
              <button type="button" onClick={downloadQr} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[hsl(var(--border))] text-[11px] uppercase tracking-[0.16em] hover:border-[hsl(var(--accent))]" data-testid="card-download-qr">
                <Download className="h-3.5 w-3.5" /> Descarregar QR (PNG)
              </button>
              <a href={cardUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 border border-[hsl(var(--border))] text-[11px] uppercase tracking-[0.16em] hover:border-[hsl(var(--accent))]" data-testid="card-open-link">
                <ExternalLink className="h-3.5 w-3.5" /> Ver cartão
              </a>
            </div>

            <div className="mt-5 space-y-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">O que mostrar no cartão</div>
              <Toggle label="Email profissional" checked={card.card_show_email} onChange={(v) => patch({ card_show_email: v })} testid="card-show-email" />
              <Toggle label="Telemóvel" checked={card.card_show_phone} onChange={(v) => patch({ card_show_phone: v })} testid="card-show-phone" />
              <Toggle label="LinkedIn" checked={card.card_show_linkedin} onChange={(v) => patch({ card_show_linkedin: v })} testid="card-show-linkedin" />
              <Toggle label="Perfil MBTI" checked={card.card_show_mbti} onChange={(v) => patch({ card_show_mbti: v })} testid="card-show-mbti" />
              <Toggle label="Valores VIA-ME" checked={card.card_show_via} onChange={(v) => patch({ card_show_via: v })} testid="card-show-via" />
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
